import _range from "lodash/range";
import _isNil from "lodash/isNil";
import _inRange from "lodash/inRange";
import { useEffect, useState } from 'react';
import { ActionBar, ContainedIconButton, FlatButton } from '@signavio/ui';
import { useIsSM, useIsXS } from '../../hooks';
import i18n from '@signavio/i18n';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function Pagination({
  currentPage,
  pages,
  onChange
}) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);
  const memoizedPages = usePages(pages);
  const smScreen = useIsSM();
  const xsScreen = useIsXS();
  const smallScreen = xsScreen || smScreen;
  if (memoizedPages < 2) {
    return null;
  }
  return /*#__PURE__*/_jsxs(ActionBar, {
    children: [/*#__PURE__*/_jsx(ActionBar.Group, {
      children: /*#__PURE__*/_jsx(ActionBar.Item, {
        children: /*#__PURE__*/_jsx(ContainedIconButton, {
          small: true,
          icon: "nav-prev",
          onClick: () => onChange(currentPage - 1),
          disabled: currentPage === 0,
          "aria-label": i18n('Previous Page')
        })
      })
    }), /*#__PURE__*/_jsx(ActionBar.Group, {
      children: _range(memoizedPages).map(page => {
        const asNumber = shouldDisplayAsNumber(currentPage, memoizedPages, page, smallScreen);
        const previousPageEllipsed = page > 0 && !shouldDisplayAsNumber(currentPage, memoizedPages, page - 1, smallScreen);
        const nextPageEllipsed = !shouldDisplayAsNumber(currentPage, memoizedPages, page + 1, smallScreen);
        if (!asNumber) {
          if (previousPageEllipsed) {
            return undefined;
          }
          if (nextPageEllipsed) {
            return /*#__PURE__*/_jsx(ActionBar.Item, {
              children: /*#__PURE__*/_jsx(FlatButton, {
                disabled: true,
                small: true,
                onClick: () => {},
                "data-testid": "page",
                children: "..."
              })
            }, page);
          }
        }
        return /*#__PURE__*/_jsx(ActionBar.Item, {
          children: /*#__PURE__*/_jsx(FlatButton, {
            small: true,
            active: page === currentPage,
            "aria-current": page === currentPage,
            onClick: () => onChange(page),
            "data-testid": `page-${page + 1}-${page === currentPage ? 'active' : 'inactive'}`,
            children: page + 1
          })
        }, page);
      }).filter(element => !!element)
    }), /*#__PURE__*/_jsx(ActionBar.Group, {
      children: /*#__PURE__*/_jsx(ActionBar.Item, {
        children: /*#__PURE__*/_jsx(ContainedIconButton, {
          small: true,
          icon: "nav-next",
          disabled: currentPage === memoizedPages - 1,
          onClick: () => onChange(currentPage + 1),
          "aria-label": i18n('Next Page')
        })
      })
    })]
  });
}
function shouldDisplayAsNumber(currentPage, memoizedPages, page, smallScreen) {
  const isLast = memoizedPages - 1;
  const isSecondLast = memoizedPages - 2;
  if (smallScreen) {
    if (page === currentPage) {
      return true;
    }
    if (page === 0) {
      return true;
    }
    if (page === isLast) {
      // small screen 1, ..., 99, 100
      return true;
    }
    if (currentPage === 0 || currentPage === 1) {
      // 1, 2, 3, ..., 100
      return page < 3;
    }
    if (currentPage === isLast || currentPage === isSecondLast) {
      // small screen 1, ..., 98, 99, 100
      return page > memoizedPages - 4;
    }

    // smallScreen 1, ..., current, ... 100
    return false;
  }
  if (page === currentPage) {
    return true;
  }
  if (page === 0 || page === 1) {
    return true;
  }
  if (page === isLast || page === isSecondLast) {
    return true;
  }
  if (currentPage === 0 || currentPage === 1) {
    // 1, 2, 3, 4, 5, ..., 99, 100
    return page < 5;
  }
  if (currentPage === isLast || currentPage === isSecondLast) {
    // 1, 2, ..., 96, 97, 98, 99, 100
    return page > memoizedPages - 6;
  }

  // 1, 2, ..., 53, 54, 55, 56, 57, ..., 99, 100
  return _inRange(page, currentPage - 2, currentPage + 3);
}
const usePages = pages => {
  // memoize pages to avoid flickering in case pages prop changes through
  // and undefined state
  const [memoizedPages, setPages] = useState(pages === 0 ? 0 : pages || 1);
  useEffect(() => {
    if (!_isNil(pages) && pages !== memoizedPages) {
      setPages(pages === 0 ? 0 : pages || 1);
    }
  }, [pages, memoizedPages]);
  return memoizedPages;
};
export default Pagination;